import { useEffect, useState } from "react";
import { AlertTriangle, Send } from "lucide-react";
import { Modal } from "./Modal";
import { request } from "./FunctionalApp";

async function api(path: string, init: RequestInit = {}) {
  const r = await request(path, init);
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(String(body.error || "Error").replaceAll("_", " "));
  return body;
}

function snapToPackSize(qty: number, packSize: number | null): number {
  if (!packSize || packSize <= 0) return qty;
  return Math.ceil(qty / packSize) * packSize;
}

export function LowStockWidget({ setSection }: { setSection?: (s: string) => void }) {
  const [parts, setParts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [showPo, setShowPo] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    request("/api/cmms/inventory/low-stock")
      .then(async r => {
        if (r.ok) {
          const d = await r.json();
          const pts = d.parts || [];
          setParts(pts);
          // Pre-select all parts that have a supplier email
          const sel: Record<string, boolean> = {};
          const qty: Record<string, string> = {};
          for (const p of pts) {
            sel[p.id] = !!p.supplier_email;
            const packSize = Number(p.pack_size) || null;
            const orderQty = Number(p.order_qty) || Number(p.reorder_point) || 1;
            qty[p.id] = String(snapToPackSize(orderQty, packSize));
          }
          setSelected(sel);
          setQuantities(qty);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function updateQty(partId: string, val: string, packSize: number | null) {
    const num = Number(val);
    if (!isNaN(num) && num > 0 && packSize && packSize > 0) {
      setQuantities(q => ({...q, [partId]: String(snapToPackSize(num, packSize))}));
    } else {
      setQuantities(q => ({...q, [partId]: val}));
    }
  }

  const selectedParts = parts.filter(p => selected[p.id]);
  const bySupplier = selectedParts.reduce((acc: any, p: any) => {
    const key = p.supplier_email || "no-email";
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});
  const suppliersWithEmail = Object.keys(bySupplier).filter(k => k !== "no-email").length;

  async function sendOrders() {
    setSending(true);
    try {
      const orders = selectedParts
        .filter(p => p.supplier_email)
        .map(p => ({
          partId: p.id,
          canonicalName: p.canonical_name,
          supplierEmail: p.supplier_email,
          supplierName: p.supplier_name || "",
          supplierRef: p.supplier_ref || p.primary_code || "",
          quantity: quantities[p.id] || p.reorder_point,
          packSize: p.pack_size || null,
        }));
      const r = await api("/api/cmms/inventory/purchase-orders", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ orders }),
      });
      setMessage(`✓ ${r.emailsSent} purchase order${r.emailsSent !== 1 ? "s" : ""} sent`);
      setShowPo(false);
    } catch (err: any) {
      setMessage(err.message);
    } finally {
      setSending(false);
    }
  }

  if (loading || parts.length === 0) return null;

  return (
    <>
      <section className="func-panel low-stock-widget">
        <div className="func-table-head">
          <h2><AlertTriangle size={16} style={{color:"#f59e0b",marginRight:6}} />Low stock — reorder list</h2>
          <span style={{color:"var(--muted)",fontSize:13}}>{parts.length} part{parts.length !== 1 ? "s" : ""} at or below reorder point</span>
          <button
            disabled={selectedParts.filter(p => p.supplier_email).length === 0}
            onClick={() => setShowPo(true)}
          >
            <Send size={14} /> Send orders ({selectedParts.filter(p => p.supplier_email).length})
          </button>
        </div>
        {message && <div className="func-admin-note" style={{margin:"0 16px 8px"}}>{message}</div>}
        <div className="func-list" style={{"--cols": "32px 1fr 80px 80px 130px 120px"} as any}>
          <div className="func-list-row func-th">
            <input type="checkbox"
              checked={parts.every(p => selected[p.id])}
              onChange={e => setSelected(Object.fromEntries(parts.map(p => [p.id, e.target.checked])))}
              style={{accentColor:"#0b9f91"}}
            />
            <span>Part</span>
            <span>Stock</span>
            <span>Reorder</span>
            <span>Order qty</span>
            <span>Supplier</span>
          </div>
          {parts.map((p: any) => {
            const stock = Number(p.current_stock);
            const packSize = Number(p.pack_size) || null;
            const critical = stock <= 0;
            return (
              <div key={p.id} className="func-list-row" style={{opacity: selected[p.id] ? 1 : 0.55}}>
                <input type="checkbox"
                  checked={!!selected[p.id]}
                  onChange={e => setSelected(s => ({...s, [p.id]: e.target.checked}))}
                  style={{accentColor:"#0b9f91"}}
                />
                <span>
                  <b>{p.canonical_name}</b>
                  <small>{p.primary_code}{p.manufacturer ? ` · ${p.manufacturer}` : ""}{packSize ? ` · Pack: ${packSize}` : ""}</small>
                </span>
                <span style={{fontWeight:700, color: critical ? "#dc2626" : "#f59e0b"}}>
                  {critical ? "OUT" : stock.toFixed(0)}
                </span>
                <span>{Number(p.reorder_point).toFixed(0)}</span>
                <span>
                  <input
                    type="number" min="0" step={packSize || 1}
                    value={quantities[p.id] || ""}
                    onChange={e => updateQty(p.id, e.target.value, packSize)}
                    onBlur={e => updateQty(p.id, e.target.value, packSize)}
                    style={{width:"100%",padding:"5px 8px",border:"1px solid #dce6ea",borderRadius:5,font:"inherit",fontSize:13}}
                  />
                  {packSize && <small style={{color:"var(--muted)",fontSize:10}}>Pack size: {packSize}</small>}
                </span>
                <span style={{fontSize:12}}>
                  {p.supplier_name
                    ? <><b>{p.supplier_name}</b>{p.supplier_email && <small style={{display:"block",color:"var(--muted)"}}>{p.supplier_email}</small>}</>
                    : <span style={{color:"var(--muted)"}}>No supplier <button className="secondary" style={{fontSize:11,padding:"2px 6px"}} onClick={() => setSection?.("Inventory")}>Add</button></span>
                  }
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {showPo && (
        <Modal title={`Send purchase orders (${suppliersWithEmail} supplier${suppliersWithEmail !== 1 ? "s" : ""})`} onClose={() => setShowPo(false)} width={560}>
          <p style={{margin:0,color:"#506573",fontSize:14}}>
            This will send {suppliersWithEmail} email{suppliersWithEmail !== 1 ? "s" : ""}, one per supplier, listing all selected parts.
          </p>
          {Object.entries(bySupplier).filter(([k]) => k !== "no-email").map(([email, pts]: any) => (
            <div key={email} style={{background:"#f5f8fa",borderRadius:8,padding:"10px 14px",display:"grid",gap:4}}>
              <b style={{fontSize:13}}>{pts[0].supplier_name || email}</b>
              <small style={{color:"var(--muted)"}}>{email}</small>
              {pts.map((p: any) => (
                <div key={p.id} style={{fontSize:13,display:"flex",justifyContent:"space-between"}}>
                  <span>{p.canonical_name}{p.supplier_ref ? ` (${p.supplier_ref})` : ""}</span>
                  <b>×{quantities[p.id]}</b>
                </div>
              ))}
            </div>
          ))}
          {bySupplier["no-email"]?.length > 0 && (
            <p style={{color:"#f59e0b",margin:0,fontSize:13}}>
              ⚠️ {bySupplier["no-email"].length} selected part{bySupplier["no-email"].length !== 1 ? "s have" : " has"} no supplier email and will be skipped.
            </p>
          )}
          <button disabled={sending} onClick={sendOrders}>
            <Send size={14} /> {sending ? "Sending…" : `Send ${suppliersWithEmail} order${suppliersWithEmail !== 1 ? "s" : ""}`}
          </button>
        </Modal>
      )}
    </>
  );
}
